import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";

import { IContentUpdatePayload, IContentCreatePayload, IContentListResponse, IContentsParams } from "@/api/types/content";
import { contentService, GetContents } from "@/api/services/content";
import { IPaginatedRequestBody } from "@/types/axios-response";

export function useContentById(id: number) {
    return useQuery({
        enabled: !!id,
        queryKey: ["contents", id],
        queryFn: () => contentService.show(Number(id)),
    });
}

export function useUpdateContent() {
    const queryClient = useQueryClient();
    const { enqueueSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: ({ id, body }: { id: number, body: IContentUpdatePayload }) => {
            return contentService.update(Number(id), body);
        },
        onError: (error) => {
            enqueueSnackbar(error.message, { variant: "error" });
        },
        onSuccess: (_, { id }) => {
            queryClient.invalidateQueries({ queryKey: ["contents", id] });
            enqueueSnackbar("SuccessFully Updated", { variant: "success" });
        },
    });
}

export function useCreateContent() {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (body: IContentCreatePayload) => contentService.create(body),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["contents"] });
            enqueueSnackbar("Successfully Created", { variant: "success" });
        },
        onError: (error) => {
            enqueueSnackbar(error.message, { variant: "error" });
        },
    });
}

export function useContentsList(params: IContentsParams) {
    return useQuery({ queryKey: ["contents", params], queryFn: () => GetContents(params) });
}

export function useContentList(id?: number) {
    const { enqueueSnackbar } = useSnackbar();
    const [contentList, setContentList] = useState<IContentListResponse[]>([]);

    const { mutate: getContentList, ...rest } = useMutation({
        mutationFn: (body: IPaginatedRequestBody) => contentService.list(body),
        onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });

    const reFetch = useCallback(() => {
        getContentList(
            { take: 50, skip: 0, filter: { parentId: id } },
            {
                onSuccess: (response) => {
                    setContentList(response.data.data.items);
                },
            }
        );
    }, [getContentList, id]);

    useEffect(() => {
        if (id) reFetch();
    }, [reFetch, id]);

    return { contentList, getContentList, reFetch, ...rest };
}

export function useDeleteContent() {
    const { enqueueSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    const { mutate: deleteContent, ...rest } = useMutation({
        mutationFn: (id: number) => contentService.delete(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["contents"] });
        },
        onError: (error) => enqueueSnackbar(error.message, { variant: "error" }),
    });

    return { deleteContent, ...rest };
}
